import { useViewModel } from '@descomplica/microfrontend-react';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { Button } from '@/shared/presentation/components/ui/Button';
import { DataTable } from '@/shared/presentation/components/ui/DataTable';
import { FormModal } from '@/shared/presentation/components/ui/FormModal';
import { Input } from '@/shared/presentation/components/ui/Input';
import { LoadingModal } from '@/shared/presentation/components/ui/LoadingDialog';
import { Page } from '@/shared/presentation/components/ui/Page';
import { Textarea } from '@/shared/presentation/components/ui/Textarea';

import { RolesViewModel } from './Roles.vm';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

const RoleForm = observer(({ isUpdating }: { isUpdating?: boolean }) => {
  const vm = useViewModel(RolesViewModel);

  return (
    <Form>
      <Input
        label="Nome"
        name="name"
        errorMessage={vm.form.errors.name && vm.form.errors.name[0]}
        onChange={(value) => vm.form.setValue('name', value)}
        onBlur={() => vm.form.validateField('name')}
        value={vm.form.values.name}
        disabled={isUpdating}
      />
      <Input
        label="Slug"
        name="slug"
        errorMessage={vm.form.errors.slug && vm.form.errors.slug[0]}
        onChange={(value) => vm.form.setValue('slug', value)}
        onBlur={() => vm.form.validateField('slug')}
        value={vm.form.values.slug}
        disabled={isUpdating}
      />
      <Textarea
        label="Descrição"
        name="description"
        errorMessage={vm.form.errors.description && vm.form.errors.description[0]}
        onChange={(value) => vm.form.setValue('description', value)}
        onBlur={() => vm.form.validateField('description')}
        value={vm.form.values.description}
      />
    </Form>
  );
});

const CreateRole = observer(() => {
  const vm = useViewModel(RolesViewModel);

  return (
    <FormModal
      title="Adicionar papel"
      onClose={() => vm.closeCreateRoleModal()}
      onSubmit={() => vm.create()}
      isSubmitting={vm.isSubmitting}
      isValid={vm.form.isValid}
    >
      <RoleForm />
    </FormModal>
  );
});

const UpdateRole = observer(() => {
  const vm = useViewModel(RolesViewModel);

  return (
    <FormModal
      title="Editar papel"
      onClose={() => vm.closeUpdateRoleModal()}
      onSubmit={() => vm.updateRole()}
      isSubmitting={vm.isUpdating}
      isValid={vm.form.isValid}
    >
      <RoleForm isUpdating={vm.isUpdating} />
    </FormModal>
  );
});

export const RolesScreen = observer(() => {
  const vm = useViewModel(RolesViewModel);

  const columns = useMemo(
    () => [
      {
        name: 'Nome',
        selector: (row) => row.name,
        sortable: true,
      },
      {
        name: 'Slug',
        selector: (row) => row.slug,
      },
      {
        name: 'Descrição',
        selector: (row) => row.description || '',
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      {
        name: 'Editar',
        onClick: (row) => {
          vm.openUpdateRoleModal(row);
        },
      },
      {
        name: 'Excluir',
        onClick: (row) => {
          const confirm = window.confirm('Tem certeza que deseja excluir?');
          if (confirm) {
            vm.removeRole(row.guid);
          }
        },
      },
    ],
    [],
  );

  return (
    <Page
      title="Papéis"
      subtitle="Gerencie os papéis do sistema"
      isLoading={vm.isLoading}
      hasError={vm.hasError}
      errorMessage={'Ocorreu um erro ao carregar os papéis.'}
      headerActions={() => (
        <Button icon="add" disabled={vm.isLoading} onClick={() => vm.openCreateRoleModal()}>
          Adicionar
        </Button>
      )}
    >
      <DataTable isLoading={vm.isLoading} columns={columns} data={vm.roles} actions={actions} />

      {vm.isCreateRoleModalOpen && <CreateRole />}
      {vm.isUpdateRoleModalOpen && <UpdateRole />}
      {vm.isRemoving && <LoadingModal text="Removendo papel..." />}
    </Page>
  );
});
